import type { CashLocationStatus, CashLocationType } from './cash-locations.dto.js';
import type { CashMovementDirection, CashMovementSourceType } from './cash-movement.dto.js';
import type { CashMovementDto } from './cash-movement.dto.js';
import type { CashSessionDto } from './cash-sessions.dto.js';
import type {
  CashVarianceActionDto,
  CashVarianceActionType,
  CashVarianceCaseDto,
  CashVarianceCaseStatus,
} from './cash-variance.dto.js';

export const cashAdminIncidentTypeValues = [
  'shortage',
  'overage',
  'openingMismatch',
  'closeMismatch',
  'forceCloseCounted',
  'forceCloseUnverified',
] as const;
export type CashAdminIncidentType = (typeof cashAdminIncidentTypeValues)[number];

export const cashAdminIncidentStatusValues = [
  'OPEN',
  'UNDER_REVIEW',
  'PARTIALLY_RESOLVED',
  'RESOLVED',
  'AUDIT_RISK',
] as const;
export type CashAdminIncidentStatus = CashVarianceCaseStatus | 'AUDIT_RISK';

export const cashAuditTimelineCategoryValues = [
  'cashMovement',
  'actionOnly',
  'auditRisk',
  'purchasePayout',
  'session',
] as const;
export type CashAuditTimelineCategory = (typeof cashAuditTimelineCategoryValues)[number];

export const cashAuditTimelineEventTypeValues = [
  'sessionOpened',
  'sessionClosed',
  'varianceRecognition',
  'varianceRecovery',
  'varianceWriteoff',
  'varianceOffset',
  'varianceClassification',
  'overageRemoval',
  'recoverySubmitted',
  'recoveryRejected',
  'note',
  'cashMovement',
  'forceCloseUnverified',
  'purchasePayout',
] as const;
export type CashAuditTimelineEventType = (typeof cashAuditTimelineEventTypeValues)[number];

export interface CashAuditActorRefDto {
  userId: string;
  displayName: string | null;
  email: string | null;
  employeeId: string | null;
  employeeNumber: string | null;
}

export interface CashAuditCashLocationRefDto {
  cashLocationId: string;
  name: string;
  type: CashLocationType | null;
  status: CashLocationStatus | null;
  storeId: string | null;
  storeName: string | null;
  locationId: string | null;
  locationName: string | null;
  stationId: string | null;
  stationName: string | null;
}

export interface CashAuditPurchasePayoutLinkDto {
  purchaseId: string;
  folioNumber: number | null;
  movementId: string;
  committedAt: string | null;
  cashSessionId: string | null;
  cashLocationId: string | null;
  operator: CashAuditActorRefDto | null;
  paidAmountMinor: number;
  cancellationState: 'active' | 'canceled' | 'unknown';
  canceledAt: string | null;
  technicalIds: Record<string, string>;
}

export interface CashAuditTimelineEventDto {
  id: string;
  timestamp: string;
  category: CashAuditTimelineCategory;
  eventType: CashAuditTimelineEventType;
  title: string;
  description: string | null;
  amountMinor: number | null;
  direction: CashMovementDirection | null;
  actor: CashAuditActorRefDto | null;
  cashLocationId: string | null;
  cashSessionId: string | null;
  caseId: string | null;
  actionId: string | null;
  movementId: string | null;
  purchaseId: string | null;
  sourceType: CashMovementSourceType | null;
  sourceId: string | null;
  fundingSourceCategoryId?: string | null;
  fundingSourceCategoryCodeSnapshot?: string | null;
  fundingSourceCategoryDisplayNameSnapshot?: string | null;
  technicalIds: Record<string, string>;
}

export interface CashAdminIncidentSummaryDto {
  id: string;
  companyId: string;
  caseId: string | null;
  cashSessionId: string;
  cashLocation: CashAuditCashLocationRefDto;
  incidentType: CashAdminIncidentType;
  status: CashAdminIncidentStatus;
  initialAmountMinor: number | null;
  outstandingAmountMinor: number | null;
  lastActionType: CashVarianceActionType | null;
  openedAt: string;
  lastEventAt: string;
  openedBy: CashAuditActorRefDto | null;
  lastActor: CashAuditActorRefDto | null;
  technicalIds: Record<string, string>;
}

export interface ListCashAdminIncidentsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  cashLocationId?: string;
  incidentType?: CashAdminIncidentType;
  status?: CashAdminIncidentStatus;
  actorUserId?: string;
  from?: string;
  to?: string;
  unresolvedOnly?: boolean;
}

export interface ListCashAdminIncidentsResponse {
  items: CashAdminIncidentSummaryDto[];
  actorRefs: CashAuditActorRefDto[];
}

export interface GetCashAdminIncidentAuditRequest {
  companyId: string;
  caseId?: string;
  cashSessionId?: string;
}

export interface GetCashAdminIncidentAuditResponse {
  incident: CashAdminIncidentSummaryDto;
  session: CashSessionDto;
  cashLocation: CashAuditCashLocationRefDto;
  varianceCase: CashVarianceCaseDto | null;
  actions: CashVarianceActionDto[];
  movements: CashMovementDto[];
  purchasePayoutLinks: CashAuditPurchasePayoutLinkDto[];
  actorRefs: CashAuditActorRefDto[];
  timeline: CashAuditTimelineEventDto[];
  technicalIds: Record<string, string>;
}
